import { Button, ModalV2, Select, TextArea } from '@components'
import { EinvoicingRecipientsField } from './EinvoicingRecipientsField'
import { useBusinessContext } from '@context'
import {
  EINVOICING_SHEET_LANGUAGES,
  EinvoicingSheetLanguage,
  sendEinvoicingAddressSheet,
  toHttpError
} from '@query'
import { APIError, getApiErrorMessage, setAPIErrors } from '@utils'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaPaperPlane } from 'react-icons/fa'
import { useMutation } from '@tanstack/react-query'
import styled from 'styled-components'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

interface FormValues {
  recipients: string[]
  language: EinvoicingSheetLanguage
  message: string
}

interface Props {
  isVisible: boolean
  language: EinvoicingSheetLanguage
  onClose: () => void
}

export const SendEinvoicingDetailsModal: React.FC<Props> = ({
  isVisible,
  language,
  onClose: close
}) => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()
  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      recipients: [],
      language,
      message: ''
    }
  })

  const sendMutation = useMutation({
    mutationFn: sendEinvoicingAddressSheet
  })

  const onClose = () => {
    close()
    reset()
  }

  const showSendError = (error: unknown) => {
    const httpError = toHttpError(error)
    const data = isRecord(httpError.data) ? httpError.data : undefined
    if (httpError.status === 429) {
      toast.error(getApiErrorMessage(httpError, t('settings.einvoicing.share.send.error')))
      return
    }
    if (httpError.status === 400 && data) {
      const fields: APIError = {}
      for (const key of ['recipients', 'language', 'message']) {
        if (key in data) fields[key] = data[key] as APIError[string]
      }
      if (Object.keys(fields).length > 0) {
        setAPIErrors(fields, setError)
      }
      const detail = getApiErrorMessage(httpError, '')
      if (detail) {
        toast.error(detail)
        return
      }
      if (Object.keys(fields).length > 0) return
    }
    toast.error(t('settings.einvoicing.share.send.error'))
  }

  return (
    <ModalV2
      isVisible={isVisible}
      handleOnClose={onClose}
      width={560}
      autoHeight
      header={t('settings.einvoicing.share.send.title')}
      footer={
        <ModalFooter>
          <Button type="button" intent="default" onClick={onClose}>
            {t('general.close')}
          </Button>
          <div style={{ flex: 1 }} />
          <Button
            type="submit"
            intent="success"
            form="send-einvoicing-details-form"
            icon={<FaPaperPlane />}
            showSpinner={sendMutation.isPending}
            disabled={sendMutation.isPending}
          >
            {t('settings.einvoicing.share.send.submit')}
          </Button>
        </ModalFooter>
      }
    >
      <form
        id="send-einvoicing-details-form"
        onSubmit={handleSubmit(async values => {
          try {
            const result = await sendMutation.mutateAsync({
              slug,
              recipients: values.recipients,
              language: values.language,
              message: values.message.trim() || undefined
            })
            toast.success(t('settings.einvoicing.share.send.success', { count: result.sent }))
            onClose()
          } catch (error) {
            showSendError(error)
          }
        })}
      >
        <Controller
          name="recipients"
          control={control}
          rules={{
            validate: value =>
              value.length > 0 || t('settings.einvoicing.share.send.recipientsRequired')
          }}
          render={({ field, fieldState }) => (
            <EinvoicingRecipientsField
              inputId="einvoicing-share-recipients"
              label={t('settings.einvoicing.share.send.recipients')}
              info={t('settings.einvoicing.share.send.recipientsInfo')}
              placeholder={t('settings.einvoicing.share.send.recipientsHint')}
              error={fieldState.error?.message}
              recipients={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Select
          label={t('settings.einvoicing.share.send.language')}
          id="einvoicing-share-language"
          required
          errors={errors.language}
          {...register('language')}
        >
          {EINVOICING_SHEET_LANGUAGES.map(code => (
            <option key={code} value={code}>
              {t(`settings.einvoicing.share.lang.${code}`)}
            </option>
          ))}
        </Select>
        <TextArea
          label={t('settings.einvoicing.share.send.note')}
          id="einvoicing-share-note"
          rows={3}
          placeholder={t('settings.einvoicing.share.send.notePlaceholder')}
          errors={errors.message}
          {...register('message')}
        />
      </form>
    </ModalV2>
  )
}

const ModalFooter = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
`
